import {MimeTypeMap} from "./mime_types";
import {BaseApp, ContextType} from "./BaseApp";
import {PluginApp} from "./PluginApp";
import {ProvisionedApp} from "./ProvisionedApp";
import {storageRef} from "./storage";
import {getDownloadURL, uploadBytes} from "@firebase/storage";
import {v4 as uuid} from "uuid";

export class Debouncer {

  private timer: any;

  constructor(readonly timeout: number = 1000) {
  }

  cancel(): void {
    clearTimeout(this.timer);
  }

  run(fn: () => void): void {
    clearTimeout(this.timer);
    this.timer = setTimeout(fn, this.timeout);
  }
}

export class Throttler {

  private lastRun: number;

  constructor(readonly timeout: number = 1000) {
  }

  run(fn: () => void): void {
    const now = Date.now();
    if (!this.lastRun || this.lastRun < now - this.timeout) {
      this.lastRun = now;
      fn();
    }
  }
}

export class FileUtil {

  static nameFromFile(file: File): string {
    return this.nameFromFileName(file.name);
  }

  static nameFromFileName(name: string): string {
    const index = name.lastIndexOf(".");
    if (index >= 0 && MimeTypeMap.getInstance().hasExtension((name.substring(index + 1)))) {
      return name.substring(0, index);
    }
    return name;
  }

  static extensionFromFile(file: File): string {
    return this.extensionFromFileName(file.name);
  }

  static extensionFromFileName(name: string): string {
    const index = name.lastIndexOf(".");
    let ext: string;
    if (index >= 0 && MimeTypeMap.getInstance().hasExtension((ext = name.substring(index + 1)))) {
      return ext;
    }
    return null;
  }

  static dataUrlMimeType(dataUrl: string): string {
    return dataUrl.split(',')[0].split(':')[1].split(';')[0];
  }

  static dataUrlToBlob(dataUrl: string): Blob {
    const byteString = atob(dataUrl.split(',')[1]);
    const mimeType = this.dataUrlMimeType(dataUrl);
    const buffer = new ArrayBuffer(byteString.length);
    const array = new Uint8Array(buffer);
    for (var i = 0; i < byteString.length; i++) {
      array[i] = byteString.charCodeAt(i);
    }
    return new Blob([buffer], {type: mimeType});
  }

  static async blobToDataUrl(blob: Blob): Promise<string> {
    const reader = new FileReader();
    const promise = new Promise<string>((resolve, reject) => {
      reader.onload = function (e) {
        resolve(e.target.result as string);
      }
      reader.onerror = function (e) {
        reject(e);
      }
    });
    reader.readAsDataURL(blob);
    return promise;
  }

  static async uploadBlob(blob: Blob, storedDataSource: string, storedDataPath: string | null): Promise<string> {
    let contextScope: string = "";
    if (BaseApp.CONTEXT.contextType() === ContextType.PLUGIN) {
      contextScope = "plugins/" + (BaseApp.CONTEXT as PluginApp).getPluginId() + "/";
    } else if (BaseApp.CONTEXT.contextType() === ContextType.PROVISIONED_APP) {
      contextScope = "apps/" + (BaseApp.CONTEXT as ProvisionedApp).getProvisionedAppId() + "/";
    }
    storedDataSource = contextScope + storedDataSource;
    const fileRef = storageRef(storedDataSource + "/" + (storedDataPath || (uuid() + "." + MimeTypeMap.getInstance().getExtensionFromMimeType(blob.type))));
    return new Promise((resolve, reject) => {
      uploadBytes(fileRef, blob)
        .then(snapshot => getDownloadURL(snapshot.ref))
        .then(url => resolve(url))
        .catch(err => reject(err));
    });
  }

  static async resizeImage(blob: Blob, maxWidth: number, maxHeight: number): Promise<Blob> {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(blob);

      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while maintaining aspect ratio
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((resizedBlob) => {
          resolve(resizedBlob);
        }, 'image/jpeg', 0.8); // Adjust quality as needed
      };
    });
  }
}